import UserStore from "../../stores/UserStore";
import AlertMessageStore from "../../stores/AlertMessageStore";
import { AxiosInstance, AxiosResponse } from "axios";
import { PhoneInputModel } from "../../components-v2/PhoneInput";
import { makeAutoObservable } from "mobx";
import { ApiKey, UserSettings } from "../../types/UserInfo";
import { ok, pending, PhoneNumber, PortfolioTabStatus, Result } from "../../common/common";
import { isAxiosError } from "../../api";
import { get } from "lodash";
import { isBlank } from "../../utils/strings";
import { Language } from "../../i18n/languages";

import { TMessage } from "../../i18n/tt";
import { DialogStore } from "../../dialogs/dialogDomain";
import { isCountryCodeNorway } from "../../utils/countries";
import { createEmailField, StringFieldModel } from "../../common/commonForms";

export type EditAltinnConnectionForm = {
  altinnSystemId: StringFieldModel;
  altinnSystemPassword: StringFieldModel;
  isSaving?: boolean;
};

type UpdateAltinnSystemCredentialsRequest = {
  altinnSystemId?: string;
  altinnSystemPassword?: string;
};

export enum UpdateAltinnSystemCredentialsResult {
  Ok = "Ok",
  InvalidCredentials = "InvalidCredentials",
  InvalidSystemUserPassword = "InvalidSystemUserPassword",
  ConnectionFailure = "ConnectionFailure",
}

type UpdateAltinnSystemCredentialsResponse = { result: UpdateAltinnSystemCredentialsResult };

export type EmailForm = { email: StringFieldModel; isSaving?: boolean };
export type PhoneNumberForm = { phoneNumber: PhoneInputModel; isSaving?: boolean };
export type PasswordForm = {
  oldPassword: StringFieldModel;
  newPassword: StringFieldModel;
  confirmedPassword: StringFieldModel;
  isSaving?: boolean;
};
export type UpdateEmailRequest = { email: string };
export type UpdatePhoneNumberRequest = { phoneNumber: PhoneNumber };
export type ChangePasswordRequest = { oldPassword: string; newPassword: string };

export function prepEmailForSave(form?: EmailForm): UpdateEmailRequest | undefined {
  if (!form || form.isSaving) {
    return;
  }
  form.email.commit();
  form.email.validate();
  if (!form.email.isValid) {
    return;
  }
  form.isSaving = true;
  return { email: form.email.validatedValue! };
}

export function prepPhoneNumberForSave(
  form?: PhoneNumberForm,
): UpdatePhoneNumberRequest | undefined {
  if (!form || form.isSaving) {
    return;
  }
  form.phoneNumber.validate();
  if (!form.phoneNumber.isValid) {
    return;
  }
  form.isSaving = true;
  return { phoneNumber: form.phoneNumber.toValidPhoneNumber()! };
}

export function validatePassword(value: string): TMessage | undefined {
  if (value.length < 8 || !/[A-Z]/.test(value) || !/[a-z]/.test(value) || !/\d/.test(value)) {
    return { key: "user-settings.weak-password", params: { n: 8 } };
  }
}

export function prepChangePasswordForm(): PasswordForm {
  const oldPasswordModel = new StringFieldModel({ required: true });
  const newPasswordModel = new StringFieldModel({
    required: true,
    validator: validatePassword,
  });
  const confirmedPasswordModel = new StringFieldModel({
    required: true,
    validator: (value) => {
      if (value !== newPasswordModel.inputValue) {
        return { key: "user-settings.not-matching" };
      }
    },
  });
  return {
    oldPassword: oldPasswordModel,
    newPassword: newPasswordModel,
    confirmedPassword: confirmedPasswordModel,
  };
}

export function prepChangePasswordRequest(form: PasswordForm): ChangePasswordRequest | undefined {
  if (
    !form.oldPassword.validate() ||
    !form.newPassword.validate() ||
    !form.confirmedPassword.validate()
  ) {
    return;
  }
  return {
    oldPassword: form.oldPassword.validatedValue,
    newPassword: form.newPassword.validatedValue,
  };
}

export class UserSettingsStore {
  editAltinnSystemCredentialsForm?: EditAltinnConnectionForm;
  updateAltinnSystemCredentialsResult?: UpdateAltinnSystemCredentialsResult;

  emailForm?: EmailForm;
  phoneNumberForm?: PhoneNumberForm;
  passwordForm?: PasswordForm;

  createPasswordResult?: Result;
  apiKey?: ApiKey;

  constructor(
    readonly userStore: UserStore,
    readonly messageStore: AlertMessageStore,
    readonly dialogStore: DialogStore,
    readonly http: AxiosInstance,
  ) {
    makeAutoObservable(this, { http: false }, { autoBind: true });
  }

  get email(): string {
    return this.userStore.user?.email ?? "";
  }

  get hasEmail(): boolean {
    return Boolean(this.email);
  }

  get phoneNumber(): PhoneNumber | undefined {
    return this.userStore.user?.phoneNumber;
  }

  get userName(): string {
    return this.userStore.user?.userName ?? "";
  }

  get hasPassword(): boolean {
    return this.userStore.user?.hasPassword ?? false;
  }

  get userSettings() {
    return this.userStore.userSettings;
  }

  get altinnSystemId(): string | undefined {
    return this.userSettings?.altinnSystemId;
  }

  get showAltinnSettings(): boolean {
    return (
      this.userStore.hasAnyAdminRole &&
      isCountryCodeNorway(this.userStore.user?.authenticatedCountry)
    );
  }

  get isEmailVerified(): boolean {
    return this.userStore.user?.isEmailVerified ?? false;
  }

  editEmail() {
    this.emailForm = { email: createEmailField({ value: this.email, required: true }) };
  }

  cancelEditEmail() {
    this.emailForm = undefined;
  }

  *saveEmail() {
    const payload = prepEmailForSave(this.emailForm);
    if (!payload) {
      return;
    }
    try {
      yield this.http.post("/user-settings/update-email", payload);
      if (this.userStore.user) {
        this.userStore.user.email = payload.email;
      }
      this.userStore.refreshUserInformation();
    } catch (e) {
      this.messageStore.showGeneralError({ autoHide: true });
    } finally {
      this.emailForm = undefined;
    }
  }

  startEmailVerification() {
    this.userStore.sendEmailVerificationCode(this.email);
    this.dialogStore.openVerifyEmailDialog(this.email);
  }

  *loadUserSettings() {
    try {
      const settings: AxiosResponse<UserSettings> = yield this.http.get("/user-settings");
      this.userStore.userSettings = settings.data;
    } catch (e) {
      this.messageStore.showGeneralError({ autoHide: true });
    }
  }

  *loadUserSettingsAndActivateEditAltinnSystemCredentials() {
    yield this.loadUserSettings();
    if (!this.altinnSystemId) {
      this.activateEditAltinnSystemCredentials();
    }
  }

  get hasAltinnSystemCredentials(): boolean {
    return !isBlank(this.altinnSystemId);
  }

  activateEditAltinnSystemCredentials() {
    this.editAltinnSystemCredentialsForm = {
      isSaving: false,
      altinnSystemId: new StringFieldModel({
        value: this.userSettings?.altinnSystemId,
        required: true,
      }),
      altinnSystemPassword: new StringFieldModel({ required: true }),
    };
  }

  *saveAltinnSystemCredentials() {
    const form = this.editAltinnSystemCredentialsForm;
    if (!form) {
      return;
    }
    form.altinnSystemId.validate();
    form.altinnSystemPassword.validate();
    if (!form.altinnSystemPassword.isValid || !form.altinnSystemId.isValid) {
      return;
    }

    try {
      form.isSaving = true;
      const data: UpdateAltinnSystemCredentialsRequest = {
        altinnSystemId: form.altinnSystemId.inputValue,
        altinnSystemPassword: form.altinnSystemPassword.inputValue,
      };
      const response: AxiosResponse<UpdateAltinnSystemCredentialsResponse> = yield this.http.post(
        "/user-settings/update-altinn-system-credentials",
        data,
      );
      form.isSaving = false;

      this.updateAltinnSystemCredentialsResult = response.data.result;
      if (this.updateAltinnSystemCredentialsResult === UpdateAltinnSystemCredentialsResult.Ok) {
        this.editAltinnSystemCredentialsForm = undefined;
        if (this.userSettings) {
          this.userSettings.altinnSystemId = form.altinnSystemId.inputValue;
        }
      }
    } catch (e) {
      this.messageStore.showGeneralError({ autoHide: true });
      form.isSaving = false;
    }
  }

  editPhoneNumber() {
    this.phoneNumberForm = {
      phoneNumber: new PhoneInputModel({ value: this.phoneNumber, required: true }),
    };
  }

  cancelEditPhoneNumber() {
    this.phoneNumberForm = undefined;
  }

  *savePhoneNumber() {
    const payload = prepPhoneNumberForSave(this.phoneNumberForm);
    if (!payload) {
      return;
    }
    try {
      yield this.http.post("/user-settings/update-phone-number", payload);
      if (this.userStore.user) {
        this.userStore.user.phoneNumber = payload.phoneNumber;
      }
    } catch (e) {
      this.messageStore.showGeneralError({ autoHide: true });
    } finally {
      this.phoneNumberForm = undefined;
    }
  }

  *createPassword(locale: Language) {
    this.createPasswordResult = pending();
    try {
      yield this.http.post("/user-settings/create-password", { language: locale });
      this.createPasswordResult = ok();
    } catch (e) {
      this.messageStore.showGeneralError({ autoHide: true });
      this.createPasswordResult = undefined;
    }
  }

  changePassword() {
    this.passwordForm = prepChangePasswordForm();
  }

  cancelChangePassword() {
    this.passwordForm = undefined;
  }

  *savePassword() {
    if (!this.passwordForm) {
      return;
    }
    const payload = prepChangePasswordRequest(this.passwordForm);
    if (!payload) {
      return;
    }
    try {
      this.passwordForm.isSaving = true;
      yield this.http.post("/user-settings/change-password", payload);
      this.passwordForm = undefined;
    } catch (err) {
      if (this.passwordForm) {
        this.passwordForm.isSaving = false;
      }
      if (isAxiosError(err) && get(err, ["response", "data", "error"]) === "oldPasswordIncorrect") {
        this.passwordForm?.oldPassword.setError({ key: "user-settings.old-password-incorrect" });
      } else {
        this.messageStore.showGeneralError();
      }
    }
  }

  *resetAltinnSystemCredentials() {
    this.updateAltinnSystemCredentialsResult = undefined;

    const data: UpdateAltinnSystemCredentialsRequest = {
      altinnSystemId: "",
      altinnSystemPassword: "",
    };
    try {
      yield this.http.post("/user-settings/update-altinn-system-credentials", data);
      yield this.loadUserSettings();
    } catch (e) {
      this.messageStore.showGeneralError({ autoHide: true });
    }
  }

  *setPreferredPortfolioTab(tab: PortfolioTabStatus) {
    try {
      yield this.http.put(`/user/portfolio-tab/${tab}`).then((res) => res.data);
      if (this.userSettings) {
        this.userSettings.preferredPortfolioTab = tab;
      }
    } catch (e) {
      // no action
    }
  }

  *starCompany(organisationNumber: string, accessType: string) {
    try {
      yield this.http.put(`/user/star-company/${organisationNumber}/${accessType}`);
      if (this.userSettings) {
        this.userSettings.starredCompanies = [
          ...(this.userSettings.starredCompanies ?? []),
          { organisationNumber, accessType },
        ];
      }
    } catch (e) {
      // no action
    }
  }

  *unstarCompany(organisationNumber: string, accessType: string) {
    try {
      yield this.http.put(`/user/unstar-company/${organisationNumber}/${accessType}`);
      if (this.userSettings?.starredCompanies) {
        this.userSettings.starredCompanies = this.userSettings.starredCompanies.filter(
          (x) => !(x.organisationNumber === organisationNumber && x.accessType === accessType),
        );
      }
    } catch (e) {
      // no action
    }
  }

  *createApiKey() {
    try {
      this.apiKey = yield this.http.post(`/user-settings/create-api-key`).then((res) => res.data);
      if (this.userSettings) {
        this.userSettings.apiKey = this.apiKey;
      }
    } catch (e) {
      this.messageStore.showGeneralError();
    }
  }

  closeApiKeyDialog() {
    this.apiKey = undefined;
  }
}
